import { useMemo } from "react";
import { Link } from "react-router-dom";
import { HardwarePanel } from "../components/HardwarePanel";
import { ShareDiagnosticButton } from "../components/ShareDiagnostic";
import { ModelCard } from "../components/ModelCard";
import { useCatalog } from "../lib/useCatalog";
import { useHardware } from "../context/HardwareContext";
import { bestVerdict } from "../lib/compute";
import { CheckCircle2, AlertTriangle, XCircle, ArrowRight } from "lucide-react";

function StatCard({ icon: Icon, count, label, tone, testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 ${tone}`}>
      <div className="flex items-center justify-between">
        <Icon className="h-5 w-5" />
        <span className="font-head text-3xl font-extrabold">{count}</span>
      </div>
      <p className="mt-1 text-xs font-medium">{label}</p>
    </div>
  );
}

export default function Diagnostic() {
  const { data, isLoading } = useCatalog();
  const { hardware } = useHardware();

  const stats = useMemo(() => {
    if (!data) return { can: 0, tight: 0, no: 0, top: [] };
    const models = data.models;
    let can = 0, tight = 0, no = 0;
    const scored = models.map((m) => {
      const v = bestVerdict(m, hardware, data.quantizations);
      if (v === "can_run") can++;
      else if (v === "tight") tight++;
      else no++;
      return { m, v };
    });
    const order = { can_run: 0, tight: 1, no_go: 2 };
    const top = scored
      .filter((s) => s.v !== "no_go")
      .sort((a, b) => order[a.v] - order[b.v] || b.m.params - a.m.params)
      .slice(0, 6)
      .map((s) => s.m);
    return { can, tight, no, top };
  }, [data, hardware]);

  return (
    <div className="grid-bg">
      <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
        <div className="mb-8 animate-fade-up">
          <span className="font-mono2 text-xs uppercase tracking-widest text-primary">Diagnostic matériel · LLM open source</span>
          <h1 className="mt-2 font-head text-4xl font-extrabold tracking-tight sm:text-5xl">
            Quels LLM votre machine peut-elle <span className="text-primary">vraiment</span> faire tourner&nbsp;?
          </h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground">
            Renseignez votre CPU, RAM, GPU, VRAM et accélérateur. llmetric estime la mémoire requise pour chaque
            modèle et quantification, et vous dit lesquels tournent sans effort.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <HardwarePanel />
          <div className="grid grid-cols-3 gap-3">
            <StatCard icon={CheckCircle2} count={stats.can} label="Modèles optimaux" tone="border-emerald-800 bg-emerald-950/40 text-emerald-400" testid="stat-can-run" />
            <StatCard icon={AlertTriangle} count={stats.tight} label="En charge élevée" tone="border-amber-800 bg-amber-950/40 text-amber-400" testid="stat-tight" />
            <StatCard icon={XCircle} count={stats.no} label="Hors de portée" tone="border-red-900 bg-red-950/40 text-red-400" testid="stat-no-go" />
            <div className="col-span-3 flex items-center justify-between rounded-xl border border-[#1E2638] bg-[#0F141C] p-4">
              <div>
                <p className="font-head text-sm font-bold">Explorer tout le catalogue</p>
                <p className="text-xs text-muted-foreground">Filtres par usage, taille et licence</p>
              </div>
              <Link to="/catalogue" className="flex items-center gap-1 text-sm font-semibold text-primary hover:underline" data-testid="goto-catalog">
                Catalogue <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-head text-xl font-bold">Recommandés pour votre configuration</h2>
            <ShareDiagnosticButton hardware={hardware} />
          </div>
          {isLoading ? (
            <p className="text-muted-foreground">Chargement…</p>
          ) : stats.top.length === 0 ? (
            <p className="rounded-lg border border-red-900 bg-red-950/30 p-4 text-sm text-red-400">
              Aucun modèle ne tient dans cette configuration. Augmentez la RAM/VRAM ou choisissez un preset plus puissant.
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {stats.top.map((m) => (
                <ModelCard key={m.id} model={m} quantizations={data.quantizations} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
